import React, { useState, useEffect } from 'react';
import { Building2, DollarSign, FileText } from 'lucide-react';
import { RemoteSupportService } from '../../services/remoteSupportService';
import { Modal, ModalBody, ModalFooter, Input, Button } from '../ui';

const RemoteSupportNewModal = ({ isOpen, onClose, onClientCreated, zIndex }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    dataCollectors: '',
    invoiceDescription: '',
    baseCost: '',
    additionalCost: '',
    notes: '',
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        companyName: '',
        dataCollectors: '',
        invoiceDescription: '',
        baseCost: '',
        additionalCost: '',
        notes: '',
      });
      setErrors({});
    }
  }, [isOpen]);

  // Calculate total cost
  const calculateTotalCost = () => {
    const base = parseFloat(formData.baseCost) || 0;
    const additional = parseFloat(formData.additionalCost) || 0;
    return (base + additional).toFixed(2);
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }

    // Validate cost fields if provided
    if (formData.baseCost && isNaN(parseFloat(formData.baseCost))) {
      newErrors.baseCost = "Must be a valid number";
    }

    if (formData.additionalCost && isNaN(parseFloat(formData.additionalCost))) {
      newErrors.additionalCost = "Must be a valid number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const newClient = await RemoteSupportService.createClient(formData);
      onClientCreated(newClient);
      handleClose();
    } catch (error) {
      console.error("Error creating remote support client:", error);
      setErrors({ submit: "Failed to create client. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      companyName: '',
      dataCollectors: '',
      invoiceDescription: '',
      baseCost: '',
      additionalCost: '',
      notes: '',
    });
    setErrors({});
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear related errors
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <Modal trackAs="RemoteSupportNewModal" isOpen={isOpen} onClose={handleClose} size="lg" zIndex={zIndex}>
      {/* Custom Header */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--cta-orange)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Building2 size={28} color="white" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-50)',
              }}
            >
              New Remote Support Client
            </h1>
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-1)',
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
              }}
            >
              Add a new client to the remote support program
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <ModalBody>
          {/* Error message */}
          {errors.submit && (
            <div
              style={{
                padding: 'var(--space-3)',
                marginBottom: 'var(--space-4)',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid var(--danger)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--danger)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {errors.submit}
            </div>
          )}

          {/* Two column layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
            {/* Left Column: Company Information */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Company Information
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Company Name */}
                <Input
                  label="Company Name"
                  name="companyName"
                  type="text"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Enter company name"
                  error={errors.companyName}
                  required
                  icon={<Building2 size={16} />}
                />

                {/* Data Collectors */}
                <Input
                  label="Data Collectors"
                  name="dataCollectors"
                  type="text"
                  value={formData.dataCollectors}
                  onChange={handleChange}
                  placeholder="Enter data collectors"
                />

                {/* Invoice Description */}
                <div>
                  <label
                    htmlFor="invoiceDescription"
                    style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-300)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                    }}
                  >
                    Invoice Description
                  </label>
                  <textarea
                    id="invoiceDescription"
                    name="invoiceDescription"
                    value={formData.invoiceDescription}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Enter invoice description..."
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-600)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--gray-200)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      transition: 'border-color var(--transition-fast)',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--cta-blue)';
                      e.target.style.outline = 'none';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'var(--gray-600)';
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Right Column: Pricing & Notes */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Pricing
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Base Cost */}
                <Input
                  label="Base Cost"
                  name="baseCost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.baseCost}
                  onChange={handleChange}
                  placeholder="0.00"
                  error={errors.baseCost}
                  icon={<DollarSign size={16} />}
                />

                {/* Additional Cost */}
                <Input
                  label="Additional Cost"
                  name="additionalCost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.additionalCost}
                  onChange={handleChange}
                  placeholder="0.00"
                  error={errors.additionalCost}
                  icon={<DollarSign size={16} />}
                />

                {/* Total Cost (calculated) */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-300)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                    }}
                  >
                    Total Cost
                  </label>
                  <div
                    style={{
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-600)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--success)',
                      fontSize: 'var(--text-lg)',
                      fontWeight: 'var(--font-semibold)',
                    }}
                  >
                    ${calculateTotalCost()}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label
                    htmlFor="notes"
                    style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-300)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                    }}
                  >
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Add any additional notes..."
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-600)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--gray-200)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      transition: 'border-color var(--transition-fast)',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--cta-blue)';
                      e.target.style.outline = 'none';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'var(--gray-600)';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--space-3)',
              width: '100%',
            }}
          >
            <Button variant="ghost" onClick={handleClose} type="button">
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={loading} disabled={loading}>
              {loading ? 'Creating...' : 'Create Client'}
            </Button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default RemoteSupportNewModal;
